import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import {
  Box,
  Typography,
  Button,
  Paper,
  LinearProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  AlertTitle,
  Chip,
  Tooltip,
  TextField,
  Pagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import {
  CloudUpload,
  Refresh,
  Info,
  UploadFile,
  PictureAsPdf as PdfIcon,
  TaskAlt,
} from "@mui/icons-material";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { format } from "date-fns";
import api from "../../api";
import CategoryTreeSelect from "./components/CategoryTreeSelect";
import { flattenCategoryTree } from "./components/CategoryTreeSelect";
import { SubjectChip } from "./components/QuestionBankSubjectChip";
import ImportMarkdownViewerDialog from "./components/ImportMarkdownViewerDialog";
import SimilarityReportDialog, { SimilarityChip } from "./components/SimilarityReportDialog";
import { WebSimilarityChip } from "./components/WebSimilaritySection";

/**
 * Patterns per subject key — mirrors Subject::$keyPatterns on the backend.
 * Order matters: NVR before VR to avoid "non-verbal" matching the VR pattern.
 */
const SUBJECT_KEY_PATTERNS = [
  { key: "non-verbal-reasoning", pattern: /(non[\s-]?verbal|nonverbal|\bnvr\b)/i },
  { key: "verbal-reasoning",     pattern: /\bverbal\b/i },
  { key: "maths",                pattern: /\bmaths?\b|\bmathematics\b/i },
  { key: "english",              pattern: /\benglish\b/i },
];

/**
 * Walk the selected category upward, build lineage text, then match against
 * subject key patterns. Returns the matching Subject DB record or null.
 * Accepts the live subjects list so inference reflects whatever is in the DB.
 */
const inferSubjectFromCategory = (flatCats, id, subjects) => {
  if (!id || !flatCats.length) return null;

  const byId = new Map(flatCats.map((c) => [String(c.id), c]));
  const lineageNames = [];
  const visited = new Set();
  let cat = byId.get(String(id));

  while (cat && !visited.has(String(cat.id))) {
    lineageNames.push((cat.name || "").toLowerCase());
    visited.add(String(cat.id));
    cat = cat.parent_id ? byId.get(String(cat.parent_id)) : null;
  }

  const lineageText = lineageNames.join(" ");

  for (const { key, pattern } of SUBJECT_KEY_PATTERNS) {
    if (pattern.test(lineageText)) {
      const subject = subjects.find((s) => s.key === key);
      if (subject) return subject;
    }
  }
  return null;
};

const normalizeCategoryName = (job) =>
  job.category?.name || job.category_name || "";

const QuestionBankImport = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const fileInputRef = useRef(null);
  const answerInputRef = useRef(null);

  // Upload state
  const [selectedFile, setSelectedFile] = useState(null);
  // Optional separate answers / mark-scheme PDF: its answers fill the questions and
  // students download it as the paper's mark scheme.
  const [answerFile, setAnswerFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [selectedFlow, setSelectedFlow] = useState("datalab-export-review");
  const [disableImportStylePipeline, setDisableImportStylePipeline] = useState(false);

  const [targetCategoryId, setTargetCategoryId] = useState("");
  const [targetCategoryError, setTargetCategoryError] = useState("");

  // Subjects from MasterForm — used for category-based inference
  const [subjects, setSubjects] = useState([]);
  const [schoolsList, setSchoolsList] = useState([]);
  const [examBoardsList, setExamBoardsList] = useState([]);
  const [optionalSchoolId, setOptionalSchoolId] = useState("");
  const [optionalExamBoardId, setOptionalExamBoardId] = useState("");

  // Category tree (fetched here so we can infer subject from selected category)
  const [categoryTree, setCategoryTree] = useState([]);
  const [flatCategories, setFlatCategories] = useState([]);
  // detectedSubject is a Subject DB record { id, name, key } or null
  const [detectedSubject, setDetectedSubject] = useState(null);

  // Current job tracking (after upload)
  const [currentJobId, setCurrentJobId] = useState(null);
  const [currentJobStatus, setCurrentJobStatus] = useState("");
  const [currentJobMarkerStatus, setCurrentJobMarkerStatus] = useState("");
  const [currentJobProgress, setCurrentJobProgress] = useState(0);
  const [isJobRunning, setIsJobRunning] = useState(false);

  // Recent imports
  const [recentJobs, setRecentJobs] = useState([]);
  const [loadingJobs, setLoadingJobs] = useState(false);
  const [refreshingJobId, setRefreshingJobId] = useState(null);
  const [rescanningJobId, setRescanningJobId] = useState(null);
  const [applyingUpdatesJobId, setApplyingUpdatesJobId] = useState(null);
  const [applyUpdatesResult, setApplyUpdatesResult] = useState(null); // { jobId, meta_updated, image_fixed, diagrams_generated, skipped, total }

  // MCP auto-heal indicator: set to the job ID being tracked; cleared after 32 s
  const [mcpHealingJobId, setMcpHealingJobId] = useState(null);
  const mcpHealTimersRef = useRef([]);

  // Recent imports: filter + sort by structural verification (validation_report.structural_verification)
  const [verificationFilter, setVerificationFilter] = useState("all"); // all | passed | healed | issues_found | none
  const [importSort, setImportSort] = useState("recent"); // recent | verification_first
  const [recentCategoryFilter, setRecentCategoryFilter] = useState("");
  const [recentStatusFilter, setRecentStatusFilter] = useState("all"); // all | actionable | preview | completed | confirmed | processing | failed
  const [recentSubjectFilter, setRecentSubjectFilter] = useState(""); // "" = all, else subject key
  const [recentSearchInput, setRecentSearchInput] = useState("");
  const [recentSearchDebounced, setRecentSearchDebounced] = useState("");
  const [recentJobsPage, setRecentJobsPage] = useState(1);
  const [recentJobsLastPage, setRecentJobsLastPage] = useState(1);
  const [recentJobsTotal, setRecentJobsTotal] = useState(0);
  const [letterFilter, setLetterFilter] = useState("");

  useEffect(() => {
    const t = setTimeout(() => setRecentSearchDebounced(recentSearchInput), 300);
    return () => clearTimeout(t);
  }, [recentSearchInput]);

  useEffect(() => {
    setRecentJobsPage(1);
  }, [recentSearchDebounced, recentStatusFilter, recentSubjectFilter, letterFilter]);

  const [markdownViewer, setMarkdownViewer] = useState({ open: false, importId: null, filename: "" });
  // Plagiarism: exact-duplicate upload warning (409) and per-import similarity report
  const [duplicateOf, setDuplicateOf] = useState(null);
  const [similarityDialog, setSimilarityDialog] = useState({ open: false, importId: null, filename: "" });

  // Fetch subjects from MasterForm so inference reflects the live Subject DB records
  useEffect(() => {
    api
      .get("/admin/master-form/subjects", { params: { per_page: 100 } })
      .then((res) => {
        const list = res.data?.data?.data || res.data?.data || [];
        setSubjects(Array.isArray(list) ? list.filter((s) => s.key) : []);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    api
      .get("/admin/master-form/schools", { params: { per_page: 500 } })
      .then((res) => {
        const paginated = res.data?.data;
        const list = paginated?.data ?? paginated ?? [];
        setSchoolsList(Array.isArray(list) ? list : []);
      })
      .catch(() => {});
    api
      .get("/admin/master-form/exam_boards", { params: { per_page: 200 } })
      .then((res) => {
        const paginated = res.data?.data;
        const list = paginated?.data ?? paginated ?? [];
        setExamBoardsList(Array.isArray(list) ? list : []);
      })
      .catch(() => {});
  }, []);

  // Fetch category tree once on mount so we can infer subject client-side
  useEffect(() => {
    api
      .get("/admin/mock-exam/category-tree")
      .then((res) => {
        const tree = res.data?.data || [];
        setCategoryTree(tree);
        setFlatCategories(flattenCategoryTree(tree));
      })
      .catch(() => {});
  }, []);

  // Cleanup MCP heal timers on unmount
  useEffect(() => () => mcpHealTimersRef.current.forEach(clearTimeout), []);

  // Poll current job
  useEffect(() => {
    let timer;
    if (currentJobId && isJobRunning) {
      timer = setInterval(pollCurrentJob, 3000);
    }
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentJobId, isJobRunning]);

  const fetchRecentJobs = useCallback(async () => {
    setLoadingJobs(true);
    try {
      const params = { page: recentJobsPage, per_page: 10 };
      const q = recentSearchDebounced.trim();
      if (q) params.search = q;
      if (recentStatusFilter === "actionable") {
        params.status = "preview,completed,confirmed";
      } else if (recentStatusFilter !== "all") {
        params.status = recentStatusFilter;
      }
      if (letterFilter) params.category_letter = letterFilter;
      if (recentSubjectFilter) params.subject = recentSubjectFilter;
      const res = await api.get("/admin/question-bank/import/recent", { params });
      const payload = res.data?.data;
      if (payload && Array.isArray(payload.data)) {
        setRecentJobs(payload.data);
        setRecentJobsLastPage(Math.max(1, payload.last_page ?? 1));
        setRecentJobsTotal(payload.total ?? 0);
      } else {
        const rows = Array.isArray(payload) ? payload : [];
        setRecentJobs(rows);
        setRecentJobsLastPage(1);
        setRecentJobsTotal(rows.length);
      }
    } catch (err) {
      // silent
      setRecentJobs([]);
      setRecentJobsLastPage(1);
      setRecentJobsTotal(0);
    } finally {
      setLoadingJobs(false);
    }
  }, [recentJobsPage, recentSearchDebounced, recentStatusFilter, recentSubjectFilter, letterFilter]);

  useEffect(() => {
    fetchRecentJobs();
  }, [fetchRecentJobs]);

  // Refresh the list while any similarity check is still running so the chip updates.
  const hasPendingSimilarity = recentJobs.some((j) => j.similarity_status === "pending");
  useEffect(() => {
    if (!hasPendingSimilarity) return undefined;
    const t = setTimeout(fetchRecentJobs, 8000);
    return () => clearTimeout(t);
  }, [hasPendingSimilarity, recentJobs, fetchRecentJobs]);

  const displayedRecentJobs = useMemo(() => {
    let list = [...recentJobs];

    if (recentCategoryFilter) {
      list = list.filter((job) => normalizeCategoryName(job) === recentCategoryFilter);
    }

    if (verificationFilter !== "all") {
      list = list.filter((j) => {
        const sv = j.validation_report?.structural_verification?.status;
        if (verificationFilter === "none") return !sv;
        return sv === verificationFilter;
      });
    }
    if (importSort === "verification_first") {
      const rank = (x) => {
        const s = x.validation_report?.structural_verification?.status;
        if (s === "issues_found") return 0;
        if (s === "healed") return 1;
        if (s === "passed") return 2;
        return 3;
      };
      list.sort((a, b) => {
        const d = rank(a) - rank(b);
        if (d !== 0) return d;
        return new Date(b.created_at || 0) - new Date(a.created_at || 0);
      });
    }
    return list;
  }, [recentJobs, verificationFilter, importSort, recentCategoryFilter]);

  const recentCategoryOptions = useMemo(
    () =>
      [...new Set(recentJobs.map((job) => normalizeCategoryName(job)).filter(Boolean))].sort((a, b) =>
        a.localeCompare(b)
      ),
    [recentJobs]
  );

  // Upload handler
  const handleUpload = async (force = false) => {
    if (!selectedFile) {
      toast.error("Please select a PDF file");
      return;
    }
    if (!targetCategoryId) {
      setTargetCategoryError("Target category is required.");
      toast.error("Target category is required.");
      return;
    }
    setTargetCategoryError("");
    if (uploading || isJobRunning) return;

    setUploading(true);
    setCurrentJobStatus("");
    setCurrentJobProgress(0);

    try {
      const formData = new FormData();
      formData.append("file", selectedFile);
      if (answerFile) formData.append("answer_file", answerFile);
      formData.append("target_category_id", targetCategoryId);
      formData.append("disable_import_style_pipeline", disableImportStylePipeline ? "1" : "0");
      if (optionalSchoolId) formData.append("school_id", optionalSchoolId);
      if (optionalExamBoardId) formData.append("exam_board_id", optionalExamBoardId);
      if (force) formData.append("force", "1");

      const res = await api.post("/admin/question-bank/import/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      const importId = res.data?.data?.import_id;
      setCurrentJobId(importId);
      setCurrentJobStatus("uploaded");
      setCurrentJobProgress(5);
      setIsJobRunning(true);
      setAnswerFile(null);
      if (answerInputRef.current) answerInputRef.current.value = "";
      toast.success("PDF uploaded. Processing started.");
      fetchRecentJobs();
    } catch (err) {
      const dup = err.response?.status === 409 ? err.response?.data?.data?.duplicate_of : null;
      if (dup) {
        setDuplicateOf(dup);
      } else {
        toast.error(err.response?.data?.message || "Upload failed");
      }
    } finally {
      setUploading(false);
    }
  };

  const pollCurrentJob = async () => {
    if (!currentJobId) return;
    try {
      const res = await api.get(`/admin/question-bank/import/${currentJobId}/status`);
      const data = res.data?.data;
      setCurrentJobStatus(data?.status);
      setCurrentJobMarkerStatus(data?.marker_status || "");
      setCurrentJobProgress(data?.progress_percent || 0);

      // Propagate enrichment_complete into the recent-jobs list so the "Verifying…"
      // chip updates without a full list refresh.
      if (data?.enrichment_complete !== undefined) {
        setRecentJobs((prev) =>
          prev.map((j) =>
            j.id === currentJobId
              ? { ...j, enrichment_complete: data.enrichment_complete }
              : j
          )
        );
      }

      if (data?.status === "preview" || data?.status === "completed" || data?.status === "failed") {
        setIsJobRunning(false);
        fetchRecentJobs();
        if (data.status !== "failed") {
          // Trigger agent-fix directly so issues are healed even when the backend
          // webhook (VERIFICATION_WEBHOOK_URL) is not configured. Fail silently —
          // the MCP server may not be running in all environments.
          setMcpHealingJobId(currentJobId);
          mcpHealTimersRef.current.forEach(clearTimeout);
          const jobIdToFix = currentJobId;
          api.post(`/admin/question-bank/import/${jobIdToFix}/agent-fix`)
            .then(() => fetchRecentJobs())
            .catch(() => {})
            .finally(() => {
              const t = setTimeout(() => {
                fetchRecentJobs();
                setMcpHealingJobId((prev) => (prev === jobIdToFix ? null : prev));
              }, 8000);
              mcpHealTimersRef.current = [t];
            });
        }
      }
    } catch (err) {
      // silent
    }
  };

  // Refresh a specific job status
  const handleRefreshJob = async (jobId) => {
    setRefreshingJobId(jobId);
    try {
      const res = await api.get(`/admin/question-bank/import/${jobId}/status`);
      const data = res.data?.data;
      setRecentJobs((prev) =>
        prev.map((j) =>
          j.id === jobId
            ? { ...j, status: data?.status, marker_status: data?.marker_status, progress_percent: data?.progress_percent, error_message: data?.error_message, enrichment_complete: data?.enrichment_complete ?? j.enrichment_complete }
            : j
        )
      );
    } catch (err) {
      toast.error("Failed to refresh job");
    } finally {
      setRefreshingJobId(null);
    }
  };

  // Rescan a job (re-run AI structuring)
  const handleRescan = async (jobId) => {
    setRescanningJobId(jobId);
    try {
      await api.post(`/admin/question-bank/import/${jobId}/rescan`);
      toast.success("Rescan started");
      fetchRecentJobs();
    } catch (err) {
      toast.error(err.response?.data?.message || "Rescan failed");
    } finally {
      setRescanningJobId(null);
    }
  };

  // Apply latest processing improvements to already-confirmed questions (all questions in the paper)
  const handleApplyUpdates = async (jobId) => {
    setApplyingUpdatesJobId(jobId);
    setApplyUpdatesResult(null);
    try {
      const res = await api.post(`/admin/question-bank/import/${jobId}/apply-updates`);
      const data = res.data?.data || {};
      setApplyUpdatesResult({ jobId, ...data });
      const parts = [];
      if ((data.answers_from_markdown ?? 0) > 0) parts.push(`${data.answers_from_markdown} answer(s) filled from paper`);
      if ((data.meta_updated ?? 0) > 0) parts.push(`${data.meta_updated} diagram type(s) set`);
      if ((data.image_fixed ?? 0) > 0) parts.push(`${data.image_fixed} image(s) corrected`);
      if ((data.diagrams_generated ?? 0) > 0) parts.push(`${data.diagrams_generated} SVG(s) generated`);
      if ((data.text_updated ?? 0) > 0) parts.push(`${data.text_updated} question text(s) updated`);
      if ((data.answer_data_updated ?? 0) > 0) parts.push(`${data.answer_data_updated} answer data updated`);
      if ((data.decimal_fixed ?? 0) > 0) parts.push(`${data.decimal_fixed} decimal(s) normalised`);
      if ((data.options_fixed ?? 0) > 0) parts.push(`${data.options_fixed} multiple-select option(s) fixed`);
      if (parts.length === 0) parts.push("all questions already up-to-date");
      toast.success(`Apply Updates: ${parts.join(", ")}.`);
      fetchRecentJobs();
    } catch (err) {
      toast.error(err.response?.data?.message || "Apply updates failed");
    } finally {
      setApplyingUpdatesJobId(null);
    }
  };

  const handleViewMarkdown = useCallback((jobId, filename) => {
    setMarkdownViewer({
      open: true,
      importId: jobId,
      filename: filename || `Job #${jobId}`,
    });
  }, []);

  useEffect(() => {
    const id = searchParams.get("view_markdown");
    if (!id) return;
    const fn = searchParams.get("view_markdown_fn") || "";
    const numericId = Number(id);
    if (!Number.isFinite(numericId) || numericId <= 0) {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete("view_markdown");
          next.delete("view_markdown_fn");
          return next;
        },
        { replace: true }
      );
      return;
    }
    setMarkdownViewer({ open: true, importId: numericId, filename: fn || `Import #${numericId}` });
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("view_markdown");
        next.delete("view_markdown_fn");
        return next;
      },
      { replace: true }
    );
  }, [searchParams, setSearchParams]);

  // Navigate to review page
  const handleReview = (jobId) => {
    navigate(`/admin/question-bank/import/${jobId}/review`);
  };

  const getStatusLabel = (status, markerStatus) => {
    // When the import is processing we can show a more granular label based on
    // marker_status returned by the API.
    // marker_status lifecycle:
    //   pending           → PDF submitted to DataLab /convert
    //   processing        → DataLab OCR running (polling mode)
    //   waiting_webhook   → DataLab callback pending (webhook mode / long queue)
    //   completed         → DataLab done; AI structuring in progress
    //   failed            → DataLab or AI step failed
    if (status === "processing" && markerStatus) {
      const markerMap = {
        pending:         "Uploading to OCR…",
        processing:      "Scanning PDF…",
        waiting_webhook: "Processing PDF…",
        completed:       "AI Structuring Questions…",
        failed:          "Processing Failed",
      };
      if (markerMap[markerStatus]) return markerMap[markerStatus];
    }

    const map = {
      uploaded:  "Uploaded",
      processing: "Processing",
      preview:   "Ready for Review",
      completed: "Completed",
      failed:    "Failed",
      confirmed: "Confirmed",
    };
    return map[status] || status || "Unknown";
  };

  // Main import page
  return (
    <Box sx={{ p: 3, maxWidth: 900, mx: "auto", display: "flex", flexDirection: "column", gap: 4 }}>
      {/* Upload Card */}
      <Paper sx={{ maxWidth: 650, mx: "auto", width: "100%", borderRadius: 3, overflow: "hidden" }}>
        {/* Header */}
        <Box sx={{ p: 3, pb: 1 }}>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
            Import Questions from PDF
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Let AI analyze your PDF file and extract questions automatically.
          </Typography>
        </Box>

        {/* GPT Export Alert */}
        <Box sx={{ px: 3, pt: 2 }}>
          <Alert
            severity="info"
            icon={<UploadFile />}
            sx={{
              borderRadius: 3,
              backgroundColor: "#f5f5f5",
              border: "1px solid #e0e0e0",
              "& .MuiAlert-icon": { color: "#757575" },
            }}
          >
            <AlertTitle sx={{ fontWeight: 600 }}>Already have a GPT export?</AlertTitle>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              If you converted your exam with ChatGPT, use the dedicated workflow.
            </Typography>
            <Button variant="outlined" size="small" sx={{ textTransform: "none" }}>
              Import via GPT Export
            </Button>
          </Alert>
        </Box>

        {/* File Upload Zones: question paper (required) + answers / mark scheme (optional) */}
        <Box sx={{ px: 3, pt: 3 }}>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1.5 }}>
            <Box
              role="button"
              tabIndex={0}
              onClick={() => fileInputRef.current?.click()}
              onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && fileInputRef.current?.click()}
              sx={{
                p: 3,
                border: "2px dashed",
                borderColor: selectedFile ? "#1976d2" : "#ccc",
                bgcolor: selectedFile ? "#F3F8FD" : "transparent",
                borderRadius: 2,
                textAlign: "center",
                cursor: "pointer",
                transition: "border-color 0.2s",
                "&:hover": { borderColor: "#1976d2" },
              }}
            >
              <input
                type="file"
                accept=".pdf"
                ref={fileInputRef}
                style={{ display: "none" }}
                onChange={(e) => setSelectedFile(e.target.files[0])}
              />
              <CloudUpload sx={{ fontSize: 40, color: selectedFile ? "#1976d2" : "#9e9e9e", mb: 0.5 }} />
              <Typography variant="caption" component="div" sx={{ fontWeight: 700, color: "text.secondary", textTransform: "uppercase", letterSpacing: ".04em" }}>
                Question paper *
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ wordBreak: "break-word" }}>
                {selectedFile ? selectedFile.name : "Click to select a PDF file"}
              </Typography>
            </Box>
            <Box
              role="button"
              tabIndex={0}
              onClick={() => answerInputRef.current?.click()}
              onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && answerInputRef.current?.click()}
              sx={{
                p: 3,
                border: "2px dashed",
                borderColor: answerFile ? "#2e7d32" : "#ccc",
                bgcolor: answerFile ? "#F2F8F2" : "transparent",
                borderRadius: 2,
                textAlign: "center",
                cursor: "pointer",
                transition: "border-color 0.2s",
                "&:hover": { borderColor: "#2e7d32" },
              }}
            >
              <input
                type="file"
                accept=".pdf"
                ref={answerInputRef}
                style={{ display: "none" }}
                onChange={(e) => setAnswerFile(e.target.files[0] || null)}
              />
              <TaskAlt sx={{ fontSize: 40, color: answerFile ? "#2e7d32" : "#9e9e9e", mb: 0.5 }} />
              <Typography variant="caption" component="div" sx={{ fontWeight: 700, color: "text.secondary", textTransform: "uppercase", letterSpacing: ".04em" }}>
                Answers / mark scheme (optional)
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ wordBreak: "break-word" }}>
                {answerFile ? answerFile.name : "Click to add a separate answers PDF"}
              </Typography>
              {answerFile && (
                <Button
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    setAnswerFile(null);
                    if (answerInputRef.current) answerInputRef.current.value = "";
                  }}
                  sx={{ textTransform: "none", mt: 0.5 }}
                >
                  Remove
                </Button>
              )}
            </Box>
          </Box>
          <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 1, lineHeight: 1.5 }}>
            When the answers come as a separate PDF, add it here. Its answers fill each question, Grok double-checks
            them, and students download this original mark scheme.
          </Typography>
        </Box>

        {/* How it Works Alert */}
        <Box sx={{ px: 3, pt: 2 }}>
          <Alert
            severity="info"
            icon={<Info />}
            sx={{
              borderRadius: 3,
              backgroundColor: "#f5f5f5",
              border: "1px solid #e0e0e0",
              "& .MuiAlert-icon": { color: "#424242" },
            }}
          >
            <AlertTitle sx={{ fontWeight: 600 }}>How it Works</AlertTitle>
            <Typography variant="body2" color="text.secondary">
              Our AI will scan your PDF to identify questions and generate answers.
            </Typography>
          </Alert>
        </Box>

        {/* Category + Flow dropdowns */}
        <Box sx={{ px: 3, pt: 2 }}>
          <CategoryTreeSelect
            value={targetCategoryId}
            onChange={(id) => {
              setTargetCategoryId(id);
              if (targetCategoryError) setTargetCategoryError("");
              setDetectedSubject(inferSubjectFromCategory(flatCategories, id, subjects));
            }}
            label="Target Category *"
            size="small"
            noneLabel="Select category..."
            error={!!targetCategoryError}
            helperText={targetCategoryError}
            externalCategories={categoryTree.length > 0 ? categoryTree : null}
          />
        </Box>

        <Box sx={{ px: 3, pt: 2, display: "flex", flexDirection: "column", gap: 2 }}>
          <FormControl size="small" fullWidth>
            <InputLabel id="qb-import-school-label">School (optional)</InputLabel>
            <Select
              labelId="qb-import-school-label"
              label="School (optional)"
              value={optionalSchoolId}
              onChange={(e) => setOptionalSchoolId(e.target.value)}
              sx={{ borderRadius: 3 }}
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {schoolsList.map((s) => (
                <MenuItem key={s.id} value={String(s.id)}>
                  {s.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" fullWidth>
            <InputLabel id="qb-import-exam-board-label">Exam board (optional)</InputLabel>
            <Select
              labelId="qb-import-exam-board-label"
              label="Exam board (optional)"
              value={optionalExamBoardId}
              onChange={(e) => setOptionalExamBoardId(e.target.value)}
              sx={{ borderRadius: 3 }}
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {examBoardsList.map((eb) => (
                <MenuItem key={eb.id} value={String(eb.id)}>
                  {(eb.code ? `${eb.code} — ` : "") + (eb.name || "")}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {/* Subject badge — shown once a category is selected */}
        {targetCategoryId && (
          <Box sx={{ px: 3, pt: 1, display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
            {detectedSubject ? (
              <>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                  Subject detected:
                </Typography>
                <SubjectChip subject={detectedSubject.name} />
              </>
            ) : (
              <Typography variant="caption" color="text.secondary" sx={{ fontStyle: "italic" }}>
                Subject will be auto-detected from the PDF content after upload.
              </Typography>
            )}
          </Box>
        )}
        {detectedSubject && (
          <Box sx={{ px: 3, display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
            {["verbal-reasoning", "non-verbal-reasoning"].includes(detectedSubject.key) && (
              <Tooltip title="No reading passage will be extracted — each question is standalone" placement="top">
                <Chip
                  label="No passage extraction"
                  size="small"
                  variant="outlined"
                  sx={{ height: 20, fontSize: "0.65rem", color: "text.secondary", borderColor: "divider" }}
                />
              </Tooltip>
            )}
            {detectedSubject.key === "non-verbal-reasoning" && (
              <Tooltip title="NVR questions require image assignment. Practice/example questions will be automatically excluded. If options show no images after import, use the MCP Agent Fix button in the review screen." placement="top">
                <Chip
                  label="Image assignment required"
                  size="small"
                  variant="outlined"
                  color="warning"
                  sx={{ height: 20, fontSize: "0.65rem" }}
                />
              </Tooltip>
            )}
          </Box>
        )}

        {/* Flow dropdown */}
        <Box sx={{ px: 3, pt: 2 }}>
          <FormControl size="small" fullWidth>
            <Select
              value={selectedFlow}
              onChange={(e) => setSelectedFlow(e.target.value)}
              sx={{ borderRadius: 3 }}
            >
              <MenuItem value="datalab-export-review">Datalab export & review</MenuItem>
            </Select>
          </FormControl>
        </Box>

        <Box sx={{ px: 3, pt: 2 }}>
          <Alert
            severity={disableImportStylePipeline ? "warning" : "info"}
            sx={{
              borderRadius: 3,
              backgroundColor: disableImportStylePipeline ? "#fff8e1" : "#eef7ff",
              border: "1px solid",
              borderColor: disableImportStylePipeline ? "#ffe082" : "#cfe6ff",
              "& .MuiAlert-icon": {
                color: disableImportStylePipeline ? "#b26a00" : "#1565c0",
              },
            }}
          >
            <AlertTitle sx={{ fontWeight: 600, mb: 1 }}>Import-time style pipeline</AlertTitle>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.25 }}>
              Keep this disabled to skip the extra DataLab style/custom-pipeline call during PDF import. This reduces import cost.
              Similar-paper generation can still use its own generation-time style pipeline later when needed.
            </Typography>
            <Button
              size="small"
              variant={disableImportStylePipeline ? "contained" : "outlined"}
              color={disableImportStylePipeline ? "warning" : "primary"}
              onClick={() => setDisableImportStylePipeline((prev) => !prev)}
              sx={{ textTransform: "none" }}
            >
              {disableImportStylePipeline ? "Disabled for this import" : "Enabled for this import"}
            </Button>
          </Alert>
        </Box>

        {/* Analyze and Review Button */}
        <Box sx={{ px: 3, pt: 2, pb: 3 }}>
          <Button
            fullWidth
            variant="contained"
            onClick={() => handleUpload(false)}
            disabled={!selectedFile || !targetCategoryId || uploading || isJobRunning}
            sx={{
              height: 48,
              fontSize: "1rem",
              textTransform: "none",
              borderRadius: 3,
              backgroundColor: "#1a1a1a",
              "&:hover": { backgroundColor: "#333" },
              "&:disabled": { backgroundColor: "#999" },
            }}
          >
            {uploading ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CircularProgress size={20} color="inherit" />
                Uploading PDF...
              </Box>
            ) : isJobRunning ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CircularProgress size={20} color="inherit" />
                Processing... ({currentJobProgress}%)
              </Box>
            ) : (
              "Analyze and Review"
            )}
          </Button>
        </Box>

        {/* Current job status (after upload) */}
        {currentJobId && isJobRunning && (
          <Box sx={{ px: 3, pb: 3 }}>
            <Alert severity="info" sx={{ borderRadius: 2 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Import job #{currentJobId} — {getStatusLabel(currentJobStatus, currentJobMarkerStatus)}
              </Typography>
              <LinearProgress
                variant={currentJobMarkerStatus === "waiting_webhook" ? "indeterminate" : "determinate"}
                value={currentJobMarkerStatus === "waiting_webhook" ? undefined : currentJobProgress}
                sx={{ height: 6, borderRadius: 3 }}
              />
            </Alert>
          </Box>
        )}
      </Paper>

      {/* Recent Imports Card */}
      <Paper sx={{ borderRadius: 3, overflow: "hidden" }}>
        {/* Header */}
        <Box
          sx={{
            p: 3,
            pb: 2,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            flexWrap: "wrap",
            gap: 1.5,
          }}
        >
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Recent Imports
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Track queued and completed PDF processing jobs.
            </Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
            <TextField
              size="small"
              label="Search"
              placeholder="ID or filename…"
              value={recentSearchInput}
              onChange={(e) => setRecentSearchInput(e.target.value)}
              sx={{ minWidth: 160 }}
            />
            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel id="recent-import-category-filter-label">Paper category</InputLabel>
              <Select
                labelId="recent-import-category-filter-label"
                value={recentCategoryFilter}
                onChange={(e) => setRecentCategoryFilter(e.target.value)}
                label="Paper category"
                sx={{ borderRadius: 2, fontSize: "0.8rem" }}
              >
                <MenuItem value="">Paper category: all</MenuItem>
                {recentCategoryOptions.map((categoryName) => (
                  <MenuItem key={categoryName} value={categoryName}>
                    {categoryName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 175 }}>
              <Select
                value={recentStatusFilter}
                onChange={(e) => setRecentStatusFilter(e.target.value)}
                displayEmpty
                sx={{ borderRadius: 2, fontSize: "0.8rem" }}
              >
                <MenuItem value="actionable">Status: ready &amp; done</MenuItem>
                <MenuItem value="preview">Ready for review</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
                <MenuItem value="confirmed">Confirmed</MenuItem>
                <MenuItem value="processing">Processing</MenuItem>
                <MenuItem value="failed">Failed</MenuItem>
                <MenuItem value="all">Status: all</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 170 }}>
              <Select
                value={recentSubjectFilter}
                onChange={(e) => setRecentSubjectFilter(e.target.value)}
                displayEmpty
                sx={{ borderRadius: 2, fontSize: "0.8rem" }}
              >
                <MenuItem value="">Subject: all</MenuItem>
                {subjects.map((s) => (
                  <MenuItem key={s.key} value={s.key}>
                    {s.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <Select
                value={verificationFilter}
                onChange={(e) => setVerificationFilter(e.target.value)}
                displayEmpty
                sx={{ borderRadius: 2, fontSize: "0.8rem" }}
              >
                <MenuItem value="all">Verification: all</MenuItem>
                <MenuItem value="passed">Verified</MenuItem>
                <MenuItem value="healed">Healed</MenuItem>
                <MenuItem value="issues_found">Issues</MenuItem>
                <MenuItem value="none">No verification yet</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <Select
                value={importSort}
                onChange={(e) => setImportSort(e.target.value)}
                sx={{ borderRadius: 2, fontSize: "0.8rem" }}
              >
                <MenuItem value="recent">Sort: newest first</MenuItem>
                <MenuItem value="verification_first">Sort: issues first</MenuItem>
              </Select>
            </FormControl>
          </Box>
          <Button
            variant="outlined"
            size="small"
            startIcon={
              refreshingJobId === (recentJobs[0]?.id ?? null) ? (
                <CircularProgress size={16} />
              ) : (
                <Refresh />
              )
            }
            disabled={recentJobs.length === 0 || refreshingJobId === recentJobs[0]?.id}
            onClick={() => recentJobs[0] && handleRefreshJob(recentJobs[0].id)}
            sx={{ textTransform: "none" }}
          >
            {refreshingJobId === recentJobs[0]?.id ? "Refreshing" : "Refresh last job"}
          </Button>
          {recentJobsTotal > 0 && (
            <Typography variant="caption" color="text.secondary">
              {recentJobsTotal} import{recentJobsTotal !== 1 ? "s" : ""}
            </Typography>
          )}
        </Box>

        {/* A–Z Letter Filter */}
        <Box sx={{ px: 3, pb: 1, display: "flex", flexWrap: "wrap", gap: 0.5 }}>
          {["All", ...Array.from({ length: 26 }, (_, i) => String.fromCharCode(65 + i))].map((letter) => {
            const active = letter === "All" ? !letterFilter : letterFilter === letter;
            return (
              <Chip
                key={letter}
                label={letter}
                size="small"
                variant={active ? "contained" : "outlined"}
                color={active ? "primary" : "default"}
                onClick={() => setLetterFilter(letter === "All" ? "" : letter)}
                sx={{ minWidth: 30, fontSize: "0.7rem", height: 22, cursor: "pointer" }}
              />
            );
          })}
        </Box>

        {/* Job List */}
        <Box sx={{ px: 3, pb: 3, display: "flex", flexDirection: "column", gap: 1.5 }}>
          {loadingJobs ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 2 }}>
              <CircularProgress size={16} />
              <Typography variant="body2" color="text.secondary">
                Loading recent jobs...
              </Typography>
            </Box>
          ) : recentJobs.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
              No import jobs found yet.
            </Typography>
          ) : displayedRecentJobs.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
              No imports match the selected filters.
            </Typography>
          ) : (
            displayedRecentJobs.map((job) => (
              <Paper
                key={job.id}
                variant="outlined"
                sx={{
                  p: 1.5,
                  borderRadius: 2,
                  display: "grid",
                  gridTemplateColumns: "minmax(0, 1fr) auto",
                  gridTemplateRows: "auto auto",
                  columnGap: 1.5,
                  rowGap: 0.5,
                }}
              >
                <Box
                  sx={{
                    gridColumn: "1 / -1",
                    gridRow: 1,
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 1,
                    minWidth: 0,
                    width: "100%",
                  }}
                >
                  {job.school_logo && (
                    <Box
                      component="img"
                      src={job.school_logo}
                      alt="School logo"
                      sx={{
                        width: 28,
                        height: 28,
                        objectFit: "contain",
                        borderRadius: 0.5,
                        border: "1px solid #e0e0e0",
                        flexShrink: 0,
                        mt: 0.2,
                      }}
                    />
                  )}
                  <Tooltip title={job.source_filename || `Job #${job.id}`}>
                    <Box
                      sx={{
                        minWidth: 0,
                        flex: "1 1 0%",
                        overflowX: "auto",
                        overflowY: "hidden",
                      }}
                    >
                      <Typography
                        variant="body2"
                        component="div"
                        sx={{
                          fontWeight: 600,
                          whiteSpace: "nowrap",
                          width: "max-content",
                          maxWidth: "none",
                        }}
                      >
                        {job.source_filename || `Job #${job.id}`}
                      </Typography>
                    </Box>
                  </Tooltip>
                </Box>
                <Box
                  sx={{
                    gridColumn: 1,
                    gridRow: 2,
                    minWidth: 0,
                    display: "flex",
                    gap: 0.75,
                    alignItems: "center",
                    flexWrap: "wrap",
                  }}
                >
                    <Chip
                      label={getStatusLabel(job.status, job.marker_status)}
                      size="small"
                      sx={{
                        height: 17,
                        fontSize: "0.6rem",
                        fontWeight: 600,
                        bgcolor: {
                          uploaded: "#fff3e0", processing: "#fff3e0",
                          preview: "#e3f2fd", completed: "#e8f5e9",
                          confirmed: "#e8f5e9", failed: "#ffebee",
                        }[job.status] || "#f5f5f5",
                        color: {
                          uploaded: "#e65100", processing: "#e65100",
                          preview: "#0d47a1", completed: "#1b5e20",
                          confirmed: "#1b5e20", failed: "#b71c1c",
                        }[job.status] || "#333",
                      }}
                    />
                    {/* Background enrichment still running after "Ready for Review" */}
                    {job.status === "preview" && job.enrichment_complete === false && !job.validation_report?.structural_verification && (
                      <Tooltip title="Background verification still running — image assignments may update shortly" placement="top">
                        <Chip
                          label="Verifying…"
                          size="small"
                          sx={{ height: 17, fontSize: "0.6rem", fontWeight: 600, bgcolor: "#fff8e1", color: "#b26a00", borderColor: "#ffe082", border: "1px solid" }}
                        />
                      </Tooltip>
                    )}
                    {/* MCP auto-heal in-progress indicator */}
                    {job.id === mcpHealingJobId &&
                      job.validation_report?.structural_verification?.status === "issues_found" && (
                        <Chip
                          label="Agent fixing…"
                          size="small"
                          sx={{ height: 17, fontSize: "0.6rem", fontWeight: 600, bgcolor: "#fff8e1", color: "#e65100" }}
                        />
                    )}
                    {/* Structural verification status badge */}
                    {job.validation_report?.structural_verification && (() => {
                      const sv = job.validation_report.structural_verification;
                      const cfg = {
                        passed:       { label: "Verified", bgcolor: "#e8f5e9", color: "#1b5e20" },
                        healed:       { label: "Healed",   bgcolor: "#e3f2fd", color: "#0d47a1" },
                        issues_found: { label: `Issues (${sv.unresolved_count || "?"})`, bgcolor: "#fff3e0", color: "#e65100" },
                      }[sv.status] || null;
                      if (!cfg) return null;
                      return (
                        <Chip label={cfg.label} size="small"
                          sx={{ height: 17, fontSize: "0.6rem", fontWeight: 600, bgcolor: cfg.bgcolor, color: cfg.color }} />
                      );
                    })()}
                    {/* Separate answers / mark-scheme PDF uploaded with the paper */}
                    {job.answer_key_filename && (
                      <Tooltip title={`Answers taken from ${job.answer_key_filename}`}>
                        <Chip
                          label="Answer key"
                          size="small"
                          variant="outlined"
                          sx={{ height: 17, fontSize: "0.6rem", fontWeight: 600, bgcolor: "#F2F8F2", color: "#1b5e20", borderColor: "#a5d6a7" }}
                        />
                      </Tooltip>
                    )}
                    {/* Grok's independent answer check (GrokAnswerCrossCheckJob) */}
                    {job.validation_report?.grok_crosscheck && (() => {
                      const g = job.validation_report.grok_crosscheck;
                      if (g.status === "running") {
                        return (
                          <Chip label={`Grok checking ${g.batches_done ?? 0}/${g.batches_total ?? "?"}`} size="small"
                            sx={{ height: 17, fontSize: "0.6rem", fontWeight: 600, bgcolor: "#f5f5f5", color: "#424242" }} />
                        );
                      }
                      const disagree = g.disagree || [];
                      const tip = disagree.length
                        ? disagree.map((d) => `Q${d.paper_number ?? "?"}: key ${d.expected}, Grok ${d.grok_answer}`).join(" · ")
                        : `Grok agrees with all ${g.checked ?? 0} checked answers`;
                      return (
                        <Tooltip title={tip}>
                          <Chip
                            label={disagree.length ? `Grok ✗ ${disagree.length} to check` : g.status === "failed" ? "Grok check failed" : `Grok ✓ ${g.agree ?? 0}/${g.checked ?? 0}`}
                            size="small"
                            sx={{
                              height: 17, fontSize: "0.6rem", fontWeight: 600,
                              bgcolor: disagree.length || g.status === "failed" ? "#fff3e0" : "#e8f5e9",
                              color: disagree.length || g.status === "failed" ? "#e65100" : "#1b5e20",
                            }}
                          />
                        </Tooltip>
                      );
                    })()}
                    {/* Plagiarism / similarity score (ContentSimilarityService) */}
                    <SimilarityChip
                      job={job}
                      onClick={() => setSimilarityDialog({ open: true, importId: job.id, filename: job.source_filename })}
                    />
                    <WebSimilarityChip
                      job={job}
                      onClick={() => setSimilarityDialog({ open: true, importId: job.id, filename: job.source_filename })}
                    />
                    {job.disable_import_style_pipeline && (
                      <Tooltip title="Import-time DataLab style/custom-pipeline enrichment was skipped for this upload">
                        <Chip
                          label="Style pipeline off"
                          size="small"
                          variant="outlined"
                          sx={{
                            height: 17,
                            fontSize: "0.6rem",
                            fontWeight: 600,
                            color: "#8a5a00",
                            borderColor: "#f1c56b",
                            backgroundColor: "#fff8e1",
                          }}
                        />
                      </Tooltip>
                    )}
                    {(job.subject || job.exam_subject) && (
                      <SubjectChip subject={job.subject || job.exam_subject} />
                    )}
                    {normalizeCategoryName(job) && (
                      <Chip
                        size="small"
                        variant="outlined"
                        label={normalizeCategoryName(job)}
                        sx={{ height: 17, fontSize: "0.6rem", fontWeight: 600 }}
                      />
                    )}
                    <Chip
                      size="small"
                      variant="outlined"
                      label={`Import #${job.id}`}
                      sx={{ height: 17, fontSize: "0.6rem", fontWeight: 600, fontFamily: "monospace" }}
                    />
                    {job.school?.name && (
                      <Chip
                        size="small"
                        label={job.school.name}
                        sx={{ height: 17, fontSize: "0.6rem", fontWeight: 600 }}
                      />
                    )}
                    {(job.exam_board?.code || job.exam_board?.name) && (
                      <Chip
                        size="small"
                        variant="outlined"
                        label={job.exam_board.code || job.exam_board.name}
                        sx={{ height: 17, fontSize: "0.6rem", fontWeight: 600 }}
                      />
                    )}
                    {job.questions_imported > 0 && (
                      <Typography variant="caption" color="text.secondary">
                        {job.questions_imported} questions
                      </Typography>
                    )}
                    {job.created_at && (
                      <Typography variant="caption" color="text.secondary">
                        {format(new Date(job.created_at), "d MMM yyyy")}
                      </Typography>
                    )}
                </Box>

                <Box
                  sx={{
                    gridColumn: 2,
                    gridRow: 2,
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    flexShrink: 0,
                    justifySelf: "end",
                    alignSelf: "center",
                  }}
                >
                  {/* Progress */}
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 80 }}>
                    <LinearProgress
                      variant={job.marker_status === "waiting_webhook" ? "indeterminate" : "determinate"}
                      value={job.marker_status === "waiting_webhook" ? undefined : (job.progress_percent || 0)}
                      sx={{ width: 80, height: 6, borderRadius: 3 }}
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ minWidth: 32 }}>
                      {job.marker_status === "waiting_webhook" ? "…" : `${job.progress_percent || 0}%`}
                    </Typography>
                  </Box>

                  {/* Rescan button */}
                  {["preview", "completed", "confirmed", "failed"].includes(job.status) && (
                    <Button
                      size="small"
                      variant="outlined"
                      disabled={rescanningJobId === job.id}
                      onClick={() => handleRescan(job.id)}
                      sx={{ textTransform: "none", minWidth: 80 }}
                    >
                      {rescanningJobId === job.id ? (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                          <CircularProgress size={14} />
                          Rescanning
                        </Box>
                      ) : (
                        "Rescan"
                      )}
                    </Button>
                  )}

                  {/* Apply Updates button — re-applies latest processing to already-confirmed questions.
                      Also shown after Rescan (status reverts to "preview") when questions were previously imported. */}
                  {(["completed", "confirmed"].includes(job.status) ||
                    (job.status === "preview" && (job.questions_imported ?? 0) > 0)) && (
                    <Button
                      size="small"
                      variant="outlined"
                      color="secondary"
                      disabled={applyingUpdatesJobId === job.id}
                      onClick={() => handleApplyUpdates(job.id)}
                      sx={{ textTransform: "none", minWidth: 100 }}
                      title="Apply latest processing improvements (hub-spoke detection, diagram metadata) to the confirmed question bank records"
                    >
                      {applyingUpdatesJobId === job.id ? (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                          <CircularProgress size={14} />
                          Applying...
                        </Box>
                      ) : applyUpdatesResult?.jobId === job.id ? (
                        (() => {
                          const r = applyUpdatesResult;
                          const items = [
                            (r.meta_updated ?? 0) > 0 && { n: r.meta_updated, label: "type" },
                            (r.image_fixed ?? 0) > 0 && { n: r.image_fixed, label: "img" },
                            (r.diagrams_generated ?? 0) > 0 && { n: r.diagrams_generated, label: "svg" },
                            (r.text_updated ?? 0) > 0 && { n: r.text_updated, label: "txt" },
                            (r.answer_data_updated ?? 0) > 0 && { n: r.answer_data_updated, label: "ans" },
                            (r.decimal_fixed ?? 0) > 0 && { n: r.decimal_fixed, label: "dec" },
                            (r.options_fixed ?? 0) > 0 && { n: r.options_fixed, label: "opts" },
                          ].filter(Boolean);
                          if (items.length === 0) return "✓ up-to-date";
                          return (
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexWrap: "wrap" }}>
                              <Typography component="span" sx={{ fontSize: "0.72rem", fontWeight: 700, color: "success.main", mr: 0.25 }}>✓</Typography>
                              {items.map(({ n, label }, i) => (
                                <Box key={i} sx={{ display: "inline-flex", alignItems: "center", gap: 0.25 }}>
                                  <Box component="span" sx={{ bgcolor: "#1a1a1a", color: "#fff", fontWeight: 700, fontSize: "0.65rem", fontFamily: "monospace", px: 0.6, py: 0.1, borderRadius: 0.4, letterSpacing: "0.03em" }}>{n}</Box>
                                  <Typography component="span" sx={{ fontSize: "0.65rem", color: "text.secondary" }}>{label}</Typography>
                                  {i < items.length - 1 && <Typography component="span" sx={{ fontSize: "0.65rem", color: "text.disabled", mx: 0.15 }}>·</Typography>}
                                </Box>
                              ))}
                            </Box>
                          );
                        })()
                      ) : (
                        "Apply Updates"
                      )}
                    </Button>
                  )}

                  {/* View PDF — open uploaded PDF (QuestionBankImport.source_path / source_url) */}
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<PdfIcon />}
                    onClick={() => navigate(`/admin/question-bank/papers/${job.id}`)}
                    sx={{ textTransform: "none", minWidth: 72 }}
                    title="Open uploaded PDF"
                  >
                    View PDF
                  </Button>

                  {/* View button — markdown preview */}
                  <Button
                    size="small"
                    variant="outlined"
                    color="info"
                    onClick={() => handleViewMarkdown(job.id, job.source_filename)}
                    sx={{ textTransform: "none", minWidth: 55 }}
                    title="View extracted markdown"
                  >
                    View
                  </Button>

                  {/* Review button */}
                  {["preview", "completed", "confirmed"].includes(job.status) && (
                    <Button
                      size="small"
                      variant="outlined"
                      color="primary"
                      onClick={() => handleReview(job.id)}
                      sx={{ textTransform: "none", minWidth: 65 }}
                    >
                      Review
                    </Button>
                  )}

                </Box>
              </Paper>
            ))
          )}
        </Box>
        {recentJobsLastPage > 1 && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              py: 2,
              borderTop: 1,
              borderColor: "divider",
            }}
          >
            <Pagination
              count={recentJobsLastPage}
              page={recentJobsPage}
              onChange={(_, value) => setRecentJobsPage(value)}
              color="primary"
              size="small"
              showFirstButton
              showLastButton
            />
          </Box>
        )}
      </Paper>

      <SimilarityReportDialog
        open={similarityDialog.open}
        importId={similarityDialog.importId}
        filename={similarityDialog.filename}
        onClose={() => setSimilarityDialog({ open: false, importId: null, filename: "" })}
        onRechecked={fetchRecentJobs}
      />

      {/* Exact duplicate PDF warning (upload returned 409) */}
      <Dialog open={!!duplicateOf} onClose={() => setDuplicateOf(null)} maxWidth="xs" fullWidth>
        <DialogTitle>This PDF was already uploaded</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            An identical file (same content hash) already exists as <strong>Import #{duplicateOf?.id}</strong>.
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
            <Typography variant="body2" sx={{ wordBreak: "break-word" }}>
              <strong>File:</strong> {duplicateOf?.filename}
            </Typography>
            {duplicateOf?.category && (
              <Typography variant="body2"><strong>Category:</strong> {duplicateOf.category}</Typography>
            )}
            <Typography variant="body2">
              <strong>Status:</strong> {getStatusLabel(duplicateOf?.status)}
              {duplicateOf?.questions_imported > 0 && ` · ${duplicateOf.questions_imported} questions imported`}
            </Typography>
            {duplicateOf?.created_at && (
              <Typography variant="body2">
                <strong>Uploaded:</strong> {format(new Date(duplicateOf.created_at), "d MMM yyyy HH:mm")}
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDuplicateOf(null)} sx={{ textTransform: "none" }}>Cancel</Button>
          {["preview", "completed", "confirmed"].includes(duplicateOf?.status) && (
            <Button onClick={() => { const id = duplicateOf.id; setDuplicateOf(null); handleReview(id); }} sx={{ textTransform: "none" }}>
              Open existing
            </Button>
          )}
          <Button
            variant="contained"
            color="warning"
            onClick={() => { setDuplicateOf(null); handleUpload(true); }}
            sx={{ textTransform: "none" }}
          >
            Upload anyway
          </Button>
        </DialogActions>
      </Dialog>

      <ImportMarkdownViewerDialog
        open={markdownViewer.open}
        importId={markdownViewer.importId}
        filename={markdownViewer.filename}
        onClose={() => setMarkdownViewer({ open: false, importId: null, filename: "" })}
      />
    </Box>
  );
};

export default QuestionBankImport;
